import { useState, useEffect, useCallback } from "react";
import { getLockedRate, isRateExpired, fetchAndLockRate, fetchAndLockUsdRate, getLockedUsdRate, FALLBACK_CNY_RATE, FALLBACK_USD_RATE, useAdminSettings } from "@/stores/adminStore";

export function useExchangeRate() {
  // Subscribe reactively to locked rate changes so storefront cards refresh
  // immediately after an admin locks/overrides the exchange rate (no manual
  // page refresh needed).
  const lockedRateState = useAdminSettings(s => s.lockedRate);
  const lockedUsdState = useAdminSettings(s => s.lockedUsdRate);
  const [rate, setRate] = useState<number>(lockedRateState?.rate || FALLBACK_CNY_RATE);
  const [usdRate, setUsdRate] = useState<number>(lockedUsdState?.rate || FALLBACK_USD_RATE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const refreshRate = useCallback(async () => {
    setLoading(true);
    try {
      const [newCnyRate, newUsdRate] = await Promise.all([fetchAndLockRate(), fetchAndLockUsdRate()]);
      setRate(newCnyRate);
      setUsdRate(newUsdRate);
      setError(false);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const locked = getLockedRate();
    const lockedUsd = getLockedUsdRate();
    if (locked.lockedAt > 0 && !isRateExpired()) {
      setRate(locked.rate);
      setUsdRate(lockedUsd.rate || FALLBACK_USD_RATE);
      setLoading(false);
    } else {
      refreshRate();
    }
  }, [refreshRate]);

  // Re-sync local state when the locked rate in the admin store changes
  // (admin override, exchange-rate lock refresh, cloud sync hydration).
  useEffect(() => {
    if (lockedRateState?.rate && lockedRateState.rate > 0) setRate(lockedRateState.rate);
  }, [lockedRateState?.rate, lockedRateState?.lockedAt]);
  useEffect(() => {
    if (lockedUsdState?.rate && lockedUsdState.rate > 0) setUsdRate(lockedUsdState.rate);
  }, [lockedUsdState?.rate, lockedUsdState?.lockedAt]);

  return { rate, usdRate, loading, error, refreshRate };
}
